import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc, type AdminDevice } from '../data/people.ts'
import { accessLink, postAdmin } from '../lib/adminApi.ts'
import { publicOrigin } from '../platform/origin.ts'
import { formatLocal } from '../lib/time.ts'
import { QrSvg } from '../ui/QrSvg.tsx'
import { BackLink, Banner, Field, NotAllowed, Pill } from '../ui/components.tsx'
import { StepUpField, useSensitive } from '../ui/sensitive.tsx'
import { NoFactorBanner } from './people/Factor.tsx'

const WINDOWS = [{ key: 'h1', ms: 3_600_000 }, { key: 'h24', ms: 86_400_000 }, { key: 'd7', ms: 7 * 86_400_000 }] as const

/** Shared workstations (GK-021): enrol one with a one-time link, revoke one, and flag what a suspect one sent. The device row is created by a command
 *  first; the login is created by the Node admin endpoint after, with exactly that row's auth id.
 *  M23 item 24: the page is the direction's (device.manage); the mechanic who opens it by URL gets the card, and the list is not even asked for. */
export function DevicesPage() {
  const { t } = useTranslation()
  const { org, site, can } = useClub()
  const allowed = can('device.manage')
  const devices = useRpc<AdminDevice[]>('admin_devices', { p_org: org.org_id }, allowed)
  const s = useSensitive()
  const [label, setLabel] = useState('')
  const deviceId = useRef(newId()); const authId = useRef(newId())
  const [link, setLink] = useState<{ url: string; label: string } | null>(null)
  const [win, setWin] = useState<(typeof WINDOWS)[number]['key']>('h24')
  const [sure, setSure] = useState<string | null>(null)

  async function linkFor(id: string, name: string) {
    const out = await s.wrap(() => postAdmin<{ token_hash: string; email: string }>(`/admin/devices/${id}/enrol-link`, { org_id: org.org_id }))
    if (out) { setLink({ url: accessLink(publicOrigin() ?? '', 'kiosk', out.token_hash, out.email), label: name }); s.setOk(t('devices.linkReady')) }
  }
  async function enrol() {
    const id = deviceId.current; const name = label.trim()
    const ok = await s.command('device.enroll@1', { device_id: id, auth_user_id: authId.current, ...(name ? { label: name } : {}) }, 'devices.enrolled')
    if (!ok) return
    deviceId.current = newId(); authId.current = newId(); setLabel('')
    await linkFor(id, name || id.slice(0, 8)); void devices.reload()
  }
  async function revoke(d: AdminDevice) {
    const ok = await s.command('device.revoke@1', { device_id: d.device_id }, 'devices.revoked')
    if (ok) { await s.wrap(() => postAdmin(`/admin/devices/${d.device_id}/revoke-login`, { org_id: org.org_id })); setSure(null); void devices.reload() }
  }
  const flag = (d: AdminDevice) => s.command('review.flag_device_commands@1', { run_id: newId(), device_id: d.device_id, since: new Date(Date.now() - WINDOWS.find((w) => w.key === win)!.ms).toISOString() }, 'devices.flagged')

  if (!allowed) return <NotAllowed permission="device.manage" />
  return (
    <section className="flex flex-col gap-5" data-testid="devices-page">
      <BackLink to="/people">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('devices.title')}</h1>
      <p className="meta">{t('devices.help', { site: site.name })}</p>
      <NoFactorBanner testId="devices-no-factor" />
      <StepUpField s={s} />
      <div className="card flex flex-col gap-3">
        <h2 className="text-lg font-bold">{t('devices.enrol')}</h2>
        <Field label={t('devices.label')} htmlFor="dev-label"><input id="dev-label" className="input" maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="device-label" /></Field>
        <button className="btn btn-primary" disabled={s.busy || !s.online} onClick={() => void enrol()} data-testid="device-enrol">{t('devices.enrolGo')}</button>
      </div>
      {link && (
        <div className="card flex flex-col items-center gap-3" data-testid="enrol-link">
          <h3 className="font-bold">{link.label}</h3>
          <p className="text-sm">{t('devices.linkHelp')}</p>
          <QrSvg text={link.url} className="size-56 bg-white" title={t('devices.linkQr')} />
          <input className="input text-xs" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} data-testid="enrol-link-text" aria-label={t('devices.linkTitle')} />
          <button className="btn !min-h-11" onClick={() => setLink(null)}>{t('common.close')}</button>
        </div>
      )}
      {devices.error && <Banner tone="error">{devices.error}</Banner>}
      <ul className="flex flex-col gap-3" data-testid="device-list">
        {(devices.data ?? []).map((d) => (
          <li key={d.device_id} className="card flex flex-col gap-2" data-testid="device-row">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-lg font-bold">{d.label ?? d.device_id.slice(0, 8)}</span>
              <Pill tone={d.revoked_at ? 'red' : 'green'}>{d.revoked_at ? t('devices.revoked') : t('devices.active')}</Pill>
            </div>
            <p className="meta">{d.last_seen_at ? t('devices.lastSeen', { when: formatLocal(d.last_seen_at, site.timezone, 'dd/MM/yyyy HH:mm') }) : t('devices.neverSeen')}{d.open_session ? ` · ${t('devices.sessionOpen')}` : ''}</p>
            {!d.revoked_at && (
              <div className="flex flex-wrap gap-2">
                <button className="btn !min-h-11 !px-4" disabled={s.busy || !s.online} onClick={() => void linkFor(d.device_id, d.label ?? d.device_id.slice(0, 8))} data-testid="device-relink">{t('devices.newLink')}</button>
                {sure === d.device_id
                  ? <button className="btn btn-danger !min-h-11 !px-4" disabled={s.busy || !s.online} onClick={() => void revoke(d)} data-testid="device-revoke-confirm">{t('devices.revokeConfirm')}</button>
                  : <button className="btn !min-h-11 !px-4" onClick={() => setSure(d.device_id)} data-testid="device-revoke">{t('devices.revoke')}</button>}
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('devices.flagWindow')} htmlFor={`fw-${d.device_id}`}>
                <select id={`fw-${d.device_id}`} className="input !min-h-11" value={win} onChange={(e) => setWin(e.target.value as typeof win)}>{WINDOWS.map((w) => <option key={w.key} value={w.key}>{t(`devices.window.${w.key}`)}</option>)}</select>
              </Field>
              <button className="btn !min-h-11 !px-4" disabled={s.busy || !s.online} onClick={() => void flag(d)} data-testid="device-flag">{t('devices.flag')}</button>
            </div>
          </li>
        ))}
        {devices.data && devices.data.length === 0 && <li className="opacity-70">{t('devices.none')}</li>}
      </ul>
    </section>
  )
}
